import React, { useState, useEffect, useRef } from "react";

import { useProfile } from "../common/ProfileProvider";
import tailwindClassnames from "../common/TailwindClassnames";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProjectRoles } from "../common/Enums";
import { BeatLoader } from "react-spinners";
import SelectInput from "../common/SelectInput";
import {
  DeploymentResponseDto,
  ProjectResponseDto,
  EventHistoryItem,
  DeploymentEventSummary,
  DeploymentImportDto,
} from "../common/ObjectTypes";
import { getProjectByGuid, getProjects } from "../../api/projectsApi";
import {
  getDeploymentByGuid,
  getDeployments,
  getDeploymentEventHistory,
  getLatestDeploymentEvent,
  importDeployment,
  getDeploymentHasActivePredictionRun,
  getDeploymentHasActiveImportOrUpload,
} from "../../api/deploymentsApi";
import {
  countBlobsForDeployment,
  eventStatusChipClass,
  formatTimestampToInternational,
} from "../common/helpers";
import { getSasUrlForProject } from "../../api/uploadApi";
import { toast } from "react-toastify";
import UploadComponent from "../upload/UploadComponent";
import DeploymentEventHistory from "./DeploymentEventHistory";
import EndUploadModal from "./EndUploadModal";
import { canEndUpload } from "./endUploadRules";
import { RefreshIcon, LinkIcon } from "../common/ProjectIcons";
import { useUpload } from "../upload/UploadContext";

// LocalStorage key constants for media management page
const MEDIA_STORAGE_KEYS = {
  PROJECT: "lastSelectedMediaProject",
  DEPLOYMENT: "lastSelectedMediaDeployment",
} as const;

/**
 * The "End upload" button beside the Last Event chip (AKL-897): btnSecondary's palette at the
 * tile's compact size. Composed here rather than appended to the shared constant — two `px-*`
 * utilities on one element both land in the stylesheet and source order decides which wins
 * (the `TEXTAREA_CLASS` note in ReportProblemModal) — and the shared constant is not edited.
 */
const END_UPLOAD_BUTTON_CLASS =
  "rounded-lg border border-off-white-dark bg-white px-2.5 py-1 text-xs font-semibold text-primary-off-black transition duration-150 hover:border-greyed-text focus:outline-none focus-visible:ring-2 focus-visible:ring-storm-blue dark:focus-visible:ring-sky-blue dark:border-storm-blue-border dark:bg-transparent dark:text-primary-off-white dark:hover:border-storm-blue-light";
const MediaManagement: React.FC = () => {
  const { profile, profileLoading } = useProfile();
  const [resultsLoaded, setResultsLoaded] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projects, setProjects] = useState<ProjectResponseDto[]>([]);
  const [selectedProject, setSelectedProject] =
    useState<ProjectResponseDto | null>(null);
  const [loadingDeployments, setLoadingDeployments] = useState(false);
  const [deployments, setDeployments] = useState<DeploymentResponseDto[]>([]);
  const [selectedDeployment, setSelectedDeployment] =
    useState<DeploymentResponseDto | null>(null);
  const [filesInStorage, setFilesInStorage] = useState(0);
  const [filesInAkili, setFilesInAkili] = useState(0);
  const [lastUpdate, setLastUpdate] = useState<DeploymentEventSummary | null>(
    null,
  );
  const [sasUrl, setSasUrl] = useState<string | null>(null);
  const [loadingFilesInStorage, setLoadingFilesInStorage] = useState(false);
  const [loadingFilesInAkili, setLoadingFilesInAkili] = useState(false);
  const [loadingLastUpdate, setLoadingLastUpdate] = useState(false);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [eventHistory, setEventHistory] = useState<EventHistoryItem[]>([]);
  const [loadingEventHistory, setLoadingEventHistory] = useState(false);
  const [canImport, setCanImport] = useState(false);
  const [canUpload, setCanUpload] = useState(false);
  const [importing, setImporting] = useState(false);
  const { inProgress, deploymentId: uploadDeploymentId } = useUpload();
  const [inferenceInProgress, setInferenceInProgress] = useState(false);
  const [importOrUploadApiInProgress, setImportOrUploadApiInProgress] =
    useState(false);
  const selectedDeploymentIdRef = useRef<number | null>(null);
  const [endUploadOpen, setEndUploadOpen] = useState(false);
  // The End upload button, captured when it is clicked so the dialog can hand focus back to it
  // on close (the same moment ReportProblemModal's bus records its opener).
  const [endUploadOpener, setEndUploadOpener] = useState<HTMLElement | null>(
    null,
  );
  // Where focus lands after a successful End upload: the refresh that follows unmounts the
  // button in the same render that closes the dialog, so the dialog cannot hand focus back to
  // it. The Last Event tile is the thing that just changed and stays mounted throughout; the
  // Refresh button would be the obvious alternative but is disabled while that refresh runs,
  // and a disabled control cannot take focus (Copilot review, cycle 2).
  const lastEventTileRef = useRef<HTMLElement | null>(null);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { project_guid, deployment_guid } = useParams();

  /** Fetch initial data for profile once profile is loaded and check that user is authorized to view page. */
  useEffect(() => {
    if (!profileLoading && profile) {
      if (checkMediaManagement()) {
        setResultsLoaded(true);
      } else {
        navigate("/unauthorized", { replace: true });
      }
    }
  }, [profileLoading, profile]);

  /** Load available projects for user. */
  useEffect(() => {
    const loadProjects = async () => {
      setLoadingProjects(true);
      const temp = await getProjects({
        skip: 0,
        all_projects: true,
        permission: "UPLOAD_IMAGES",
      });
      temp.projects.sort((a, b) => {
        return a.project_name.localeCompare(b.project_name);
      });
      setProjects(temp.projects);
      setLoadingProjects(false);
    };
    if (!projectsLoaded) {
      loadProjects();
      setProjectsLoaded(true);
    }
  }, []);

  /**
   * Determine if import can be started. Import can be started if:
   * A deployment is selected, there are files in storage, the number of files
   * in Akili is less than the number of files in storage, and an upload or
   * import is not currently in progress for the selected deployment.
   */
  useEffect(() => {
    setCanImport(
      !!selectedDeployment &&
        !loadingFilesInStorage &&
        !loadingFilesInAkili &&
        filesInStorage > 0 &&
        filesInAkili < filesInStorage &&
        !inferenceInProgress &&
        !importOrUploadApiInProgress &&
        !(
          inProgress && uploadDeploymentId === selectedDeployment?.deployment_id
        ),
    );
  }, [
    filesInAkili,
    selectedDeployment,
    filesInStorage,
    loadingFilesInAkili,
    loadingFilesInStorage,
    inferenceInProgress,
    importOrUploadApiInProgress,
    inProgress,
    uploadDeploymentId,
  ]);

  /**
   * Determine if upload can be started. Upload can be started if:
   * A deployment is selected, and an upload or import is not currently in
   * progress for the selected deployment.
   */
  useEffect(() => {
    setCanUpload(
      !!selectedDeployment &&
        !loadingFilesInStorage &&
        !loadingFilesInAkili &&
        !inferenceInProgress &&
        !importOrUploadApiInProgress &&
        !importing,
    );
  }, [
    selectedDeployment,
    loadingFilesInAkili,
    loadingFilesInStorage,
    inferenceInProgress,
    importOrUploadApiInProgress,
    importing,
  ]);

  /** Load last selected media page project and deployment from local storage. */
  useEffect(() => {
    const lastSelectedProject = localStorage.getItem(
      MEDIA_STORAGE_KEYS.PROJECT,
    );
    const project = lastSelectedProject
      ? JSON.parse(lastSelectedProject)
      : null;
    const lastSelectedDeployment = localStorage.getItem(
      MEDIA_STORAGE_KEYS.DEPLOYMENT,
    );
    const deployment = lastSelectedDeployment
      ? JSON.parse(lastSelectedDeployment)
      : null;
    if (project && !project_guid) {
      if (deployment) {
        navigate(`/manage/${project.guid}/${deployment.guid}`, {
          replace: true,
        });
      } else {
        navigate(`/manage/${project.guid}`, { replace: true });
      }
    }
  }, [project_guid, deployment_guid]);

  /** Load data from URL parameters if provided. This will select the project and deployment based on the provided GUIDs. */
  useEffect(() => {
    const loadDataFromUrl = async () => {
      if (project_guid && !selectedProject) {
        const lastSelectedProject = localStorage.getItem(
          "lastSelectedMediaProject",
        );
        let project = lastSelectedProject
          ? JSON.parse(lastSelectedProject)
          : null;
        if (project === null || project?.guid !== project_guid) {
          // `getProjectByGuid` rethrows since AKL-939; any failure (a 403 included) still
          // takes the "Project not found." path below, exactly as the swallowed `undefined`
          // did before.
          try {
            project = await getProjectByGuid(project_guid);
          } catch {
            project = undefined;
          }
        }

        if (project) {
          await handleSelectProject(project, false, false);
        } else {
          toast.error(t("Project not found."));
          navigate("/manage", { replace: true });
          return;
        }
      }
      if (selectedProject && sasUrl && deployment_guid && !selectedDeployment) {
        const lastSelectedDeployment = localStorage.getItem(
          "lastSelectedMediaDeployment",
        );
        let deployment = lastSelectedDeployment
          ? JSON.parse(lastSelectedDeployment)
          : null;
        if (deployment === null || deployment?.guid !== deployment_guid) {
          deployment = await getDeploymentByGuid(deployment_guid);
        }
        if (deployment) {
          if (deployment.project_id !== selectedProject.project_id) {
            toast.error(
              t(
                "Invalid URL: Deployment does not belong to the selected project.",
              ),
            );
            navigate(`/manage/${selectedProject.guid}`, { replace: true });
            return;
          }
          await handleSelectDeployment(deployment, false);
        } else {
          toast.error(t("Deployment not found."));
          navigate(`/manage/${selectedProject.guid}`, { replace: true });
          return;
        }
      }
    };
    loadDataFromUrl();
  }, [
    deployment_guid,
    project_guid,
    selectedProject,
    selectedDeployment,
    sasUrl,
  ]);

  /**
   * Fetch event history for the selected deployment.
   * Guards against stale responses by checking selectedDeploymentIdRef so a
   * slow response for a previously selected deployment cannot overwrite the
   * event-history table with data for the wrong deployment.
   * @param deployment {DeploymentResponseDto} the selected deployment
   */
  const fetchEventHistory = async (deployment: DeploymentResponseDto) => {
    if (!deployment?.deployment_id) return;
    const deploymentId = deployment.deployment_id;
    if (selectedDeploymentIdRef.current !== deploymentId) return;
    setLoadingEventHistory(true);
    try {
      const events = await getDeploymentEventHistory(deploymentId);
      if (selectedDeploymentIdRef.current !== deploymentId) return;
      const filteredEvents = Array.isArray(events)
        ? events.filter(
            (event) => event.type === "Import" || event.type === "Upload",
          )
        : [];
      setEventHistory(filteredEvents);
    } catch (error) {
      if (selectedDeploymentIdRef.current !== deploymentId) return;
      setEventHistory([]);
    } finally {
      if (selectedDeploymentIdRef.current === deploymentId) {
        setLoadingEventHistory(false);
      }
    }
  };

  /**
   * Fetch the latest event for the selected deployment.
   * Guards against stale responses by checking selectedDeploymentIdRef.
   * @param deployment {DeploymentResponseDto} the selected deployment
   */
  const fetchLastUpdate = async (deployment: DeploymentResponseDto) => {
    if (!deployment?.deployment_id) return;
    const deploymentId = deployment.deployment_id;
    if (selectedDeploymentIdRef.current !== deploymentId) return;
    setLoadingLastUpdate(true);
    try {
      const latestEvent = await getLatestDeploymentEvent(deploymentId);
      if (selectedDeploymentIdRef.current !== deploymentId) return;
      setLastUpdate(latestEvent);
    } catch (error) {
      if (selectedDeploymentIdRef.current !== deploymentId) return;
      setLastUpdate(null);
    } finally {
      if (selectedDeploymentIdRef.current === deploymentId) {
        setLoadingLastUpdate(false);
      }
    }
  };

  /**
   * Fetch whether an active prediction run exists for the given deployment.
   * Guards against stale responses by checking selectedDeploymentIdRef.
   * @param deployment {DeploymentResponseDto} the selected deployment
   */
  const fetchInferenceInProgress = async (
    deployment: DeploymentResponseDto,
  ) => {
    if (!deployment?.deployment_id) return;
    const deploymentId = deployment.deployment_id;
    try {
      const hasActive = await getDeploymentHasActivePredictionRun(deploymentId);
      if (selectedDeploymentIdRef.current === deploymentId) {
        setInferenceInProgress(hasActive);
      }
    } catch {
      // fail silently
    }
  };

  /**
   * Fetch whether an active import or upload job exists for the given deployment via the API.
   * Guards against stale responses by checking selectedDeploymentIdRef.
   * @param deployment {DeploymentResponseDto} the selected deployment
   */
  const fetchImportOrUploadApiStatus = async (
    deployment: DeploymentResponseDto,
  ) => {
    if (!deployment?.deployment_id) return;
    const deploymentId = deployment.deployment_id;
    try {
      const hasActive =
        await getDeploymentHasActiveImportOrUpload(deploymentId);
      if (selectedDeploymentIdRef.current === deploymentId) {
        setImportOrUploadApiInProgress(hasActive);
      }
    } catch {
      // fail silently
    }
  };

  /**
   * Fetch the number of files in Akili for the selected deployment by re-fetching the deployment object from the API.
   * Guards against stale responses by checking selectedDeploymentIdRef so a
   * slow response for a previously selected deployment cannot overwrite the
   * count for the currently selected deployment.
   * @param deployment {DeploymentResponseDto} the selected deployment
   */
  const fetchFilesInAkili = async (deployment: DeploymentResponseDto) => {
    if (!deployment?.deployment_id) return;
    const deploymentId = deployment.deployment_id;
    if (selectedDeploymentIdRef.current !== deploymentId) return;
    setLoadingFilesInAkili(true);
    try {
      const refreshedDeployment = await getDeploymentByGuid(deployment.guid);
      if (selectedDeploymentIdRef.current !== deploymentId) return;
      if (refreshedDeployment) {
        setFilesInAkili(refreshedDeployment.media_asset_count);
      }
    } catch (error) {
      console.error("Error fetching files in Akili:", error);
      if (selectedDeploymentIdRef.current !== deploymentId) return;
      toast.error(t("Error loading file count for deployment."));
      setFilesInAkili(0);
    } finally {
      if (selectedDeploymentIdRef.current === deploymentId) {
        setLoadingFilesInAkili(false);
      }
    }
  };

  /**
   * Update all data for the selected deployment. This is passed to the UploadComponent to refresh data after an upload completes.
   * @param deployment {DeploymentResponseDto} the selected deployment
   */
  const updateAll = async (deployment: DeploymentResponseDto) => {
    if (deployment) {
      await Promise.all([
        fetchFilesInAkili(deployment),
        fetchFilesInStorage(deployment),
        fetchLastUpdate(deployment),
        fetchEventHistory(deployment),
        fetchInferenceInProgress(deployment),
        fetchImportOrUploadApiStatus(deployment),
      ]);
    }
  };

  /** Handles starting the import process for the selected deployment. */
  const handleImport = async () => {
    if (!selectedDeployment) {
      return;
    }
    setImporting(true);
    const importDto: DeploymentImportDto = {
      deployment_id: selectedDeployment?.deployment_id,
      file_count: filesInStorage - filesInAkili,
    };
    setCanUpload(false);
    try {
      await importDeployment(importDto);
    } catch (err) {
      console.error("Error starting import:", err);
      toast.error(t("Failed to start import. Please try again."));
      setImporting(false);
      return;
    }
    setImporting(false);
    setCanImport(false);
    toast.success(t("Import started"));
    await Promise.all([
      fetchLastUpdate(selectedDeployment),
      fetchEventHistory(selectedDeployment),
      fetchImportOrUploadApiStatus(selectedDeployment),
    ]);
  };

  /**
   * After the End upload dialog has ended a dead run (AKL-897): refresh the Last Event tile,
   * the History table and the active-import-or-upload flag — the same trio `handleImport`
   * refreshes, and the flag is what re-enables the Upload and Start Import buttons — then
   * toast. The toast comes after the refresh so the History the user looks at already shows
   * the closed row.
   */
  const handleUploadEnded = async () => {
    if (selectedDeployment) {
      await Promise.all([
        fetchLastUpdate(selectedDeployment),
        fetchEventHistory(selectedDeployment),
        fetchImportOrUploadApiStatus(selectedDeployment),
      ]);
    }
    toast.success(t("Upload ended"));
  };

  /**
   * Check if the user has permissions to manage media.
   * @returns {boolean} true if the user can manage media, false otherwise
   */
  const checkMediaManagement = (): boolean => {
    if (profile) {
      const isAdmin = profile.is_admin;
      const isProjectEditorOrContributor = profile.project_roles.some(
        (role: any) =>
          role.role === ProjectRoles.OWNER ||
          role.role === ProjectRoles.EDITOR ||
          role.role === ProjectRoles.CONTRIBUTOR,
      );
      return isAdmin || isProjectEditorOrContributor;
    }
    return false;
  };

  /**
   * Handles selecting a project by fetching the SAS URL and deployments for the project.
   * @param project {ProjectResponseDto | null} the selected project
   * @param redirect {boolean} whether to redirect to the project URL
   * @param removeLastSelectedDeployment {boolean} whether to clear the last selected deployment from local storage
   */
  const handleSelectProject = async (
    project: ProjectResponseDto | null,
    redirect: boolean,
    removeLastSelectedDeployment: boolean = true,
  ) => {
    selectedDeploymentIdRef.current = null;
    setSelectedProject(project);
    setSelectedDeployment(null);
    setFilesInAkili(0);
    setDeployments([]);
    localStorage.setItem(MEDIA_STORAGE_KEYS.PROJECT, JSON.stringify(project));
    if (removeLastSelectedDeployment) {
      localStorage.removeItem(MEDIA_STORAGE_KEYS.DEPLOYMENT);
    }

    if (project === null) {
      navigate("/manage", { replace: true });
    } else {
      if (redirect) {
        if (
          window.location.pathname.includes(`/manage/${project.guid}`) === false
        ) {
          navigate(`/manage/${project.guid}`, { replace: true });
        }
      }
      setLoadingDeployments(true);
      try {
        const sas = await getSasUrlForProject(
          project?.project_id,
          project?.guid,
        );
        setSasUrl(sas);

        const temp = await getDeployments({
          all_deployments: true,
          project_id: project.project_id,
        });
        temp.deployments.sort((a, b) => {
          return a.deployment_name.localeCompare(b.deployment_name);
        });
        setDeployments(temp.deployments);
      } catch (error) {
        console.error("Error fetching deployments:", error);
        setDeployments([]);
      } finally {
        setLoadingDeployments(false);
      }
    }
  };

  /**
   * Fetch the number of files in storage for the selected deployment.
   * Guards against stale responses by checking selectedDeploymentIdRef so a
   * slow blob-count response for a previously selected deployment cannot
   * overwrite the count for the currently selected deployment.
   * @param deployment {DeploymentResponseDto} the selected deployment
   */
  const fetchFilesInStorage = async (deployment: DeploymentResponseDto) => {
    if (!deployment?.deployment_id) return;
    const deploymentId = deployment.deployment_id;
    if (selectedDeploymentIdRef.current !== deploymentId) return;
    setLoadingFilesInStorage(true);
    try {
      if (!sasUrl) {
        console.error("SAS URL not found for the project.");
        if (selectedDeploymentIdRef.current === deploymentId) {
          setFilesInStorage(0);
        }
        return;
      }

      const count = await countBlobsForDeployment(sasUrl, deployment.guid);
      if (selectedDeploymentIdRef.current !== deploymentId) return;
      setFilesInStorage(count);
    } catch (error) {
      console.error("Error fetching files in storage:", error);
      if (selectedDeploymentIdRef.current !== deploymentId) return;
      setFilesInStorage(0);
    } finally {
      if (selectedDeploymentIdRef.current === deploymentId) {
        setLoadingFilesInStorage(false);
      }
    }
  };

  /**
   * Handles selecting a deployment and fetching files in storage and Akili.
   * @param deployment {DeploymentResponseDto | null} the selected deployment
   * @param redirect {boolean} whether to redirect to the deployment URL
   */
  const handleSelectDeployment = async (
    deployment: DeploymentResponseDto | null,
    redirect: boolean,
  ) => {
    selectedDeploymentIdRef.current = deployment?.deployment_id ?? null;
    setSelectedDeployment(deployment);
    localStorage.setItem(
      MEDIA_STORAGE_KEYS.DEPLOYMENT,
      JSON.stringify(deployment),
    );
    if (!deployment) {
      navigate(`/manage/${selectedProject?.guid}`, { replace: true });
      setFilesInAkili(0);
      setInferenceInProgress(false);
      setImportOrUploadApiInProgress(false);
      return;
    }
    setLoadingDeployments(false);
    if (deployment && selectedProject) {
      if (redirect) {
        if (
          window.location.pathname.includes(
            `/manage/${selectedProject.guid}/${deployment.guid}`,
          ) === false
        ) {
          navigate(`/manage/${selectedProject.guid}/${deployment.guid}`, {
            replace: true,
          });
        }
      }
      await updateAll(deployment);
    } else {
      setFilesInStorage(0);
      setFilesInAkili(0);
      setLastUpdate(null);
    }
  };

  const lastUpdateFull = lastUpdate?.ts
    ? formatTimestampToInternational(lastUpdate.ts)
    : null;
  // Drop the trailing ":ss" for the compact header display; the full
  // timestamp (with seconds) stays available via the title attribute.
  const lastUpdateShort = lastUpdateFull ? lastUpdateFull.slice(0, -3) : null;

  const isUploadingThisDeployment =
    inProgress && uploadDeploymentId === selectedDeployment?.deployment_id;

  // Evaluated at render (AKL-897): a tile left open crosses the stale line on its next
  // re-render — a Refresh, a fetch, any state change — which is enough for a lock hours dead.
  const showEndUpload = canEndUpload({
    latestEvent: lastUpdate,
    profile,
    isUploadingHere: isUploadingThisDeployment,
    nowMs: Date.now(),
  });

  const importHelperText = isUploadingThisDeployment
    ? t("Import is unavailable while an upload is running for this deployment.")
    : importing || importOrUploadApiInProgress
      ? t(
          "An import or upload is currently in progress for this deployment. Please try again when it is complete.",
        )
      : filesInStorage === 0
        ? t("No files in storage to import yet.")
        : filesInAkili >= filesInStorage
          ? t("All {{count}} files in storage have been imported into Akili.", {
              count: filesInStorage,
            })
          : t("{{count}} files ready to import.", {
              count: filesInStorage - filesInAkili,
            });

  return (
    <div className="flex flex-row w-full">
      <div className="ml-8 mr-4 mt-8 w-full">
        {resultsLoaded ? (
          <div>
            <div className={tailwindClassnames.pageHeaderRow}>
              <div>
                <h1 className={tailwindClassnames.pageTitle}>
                  {t("Media Management")}
                </h1>
                <p className={tailwindClassnames.pageSubtitle}>
                  {t("Upload and manage media")}
                </p>
              </div>
            </div>
            <div
              className={`${tailwindClassnames.formSectionCard} mb-5 text-left`}
            >
              <div className="flex flex-row flex-wrap items-end gap-5">
                <SelectInput
                  options={projects.map((project) => ({
                    value: project,
                    label: project.project_name,
                  }))}
                  className="w-[300px] bg-storm-blue rounded h-full shadow text-left text-sm text-primary-off-white dark:text-off-white-dark"
                  isDisabled={false}
                  isLoading={loadingProjects}
                  isClearable={true}
                  isSearchable={true}
                  value={
                    selectedProject === null
                      ? null
                      : {
                          value: selectedProject,
                          label: selectedProject?.project_name,
                        }
                  }
                  isMulti={false}
                  name={t("Project")}
                  onChange={async (e: any) => {
                    await handleSelectProject(e ? e.value : null, true, true);
                  }}
                  errors={""}
                  searchBar={false}
                />
                <SelectInput
                  options={deployments.map((deployment) => ({
                    value: deployment,
                    label: deployment.deployment_name,
                  }))}
                  className="w-[300px] bg-storm-blue rounded h-full shadow text-left text-sm"
                  isDisabled={!selectedProject}
                  isLoading={loadingDeployments}
                  isClearable={true}
                  isSearchable={true}
                  value={
                    selectedDeployment === null
                      ? null
                      : {
                          value: selectedDeployment,
                          label: selectedDeployment?.deployment_name,
                        }
                  }
                  isMulti={false}
                  name={t("Deployment")}
                  onChange={async (e: any) => {
                    await handleSelectDeployment(e ? e.value : null, true);
                  }}
                  errors={""}
                  searchBar={false}
                />
              </div>
            </div>
            {selectedDeployment && (
              <div
                className={`${tailwindClassnames.formSectionCard} text-left`}
              >
                <div className={tailwindClassnames.formSectionHeader}>
                  <div className="flex flex-row flex-wrap items-center justify-between gap-4">
                    <h2 className={tailwindClassnames.formSectionTitle}>
                      <Link
                        to={`/projects/${selectedProject?.guid}/deployments/${selectedDeployment.guid}`}
                        className="inline-flex items-center gap-2 text-base font-semibold text-storm-blue hover:underline dark:text-sky-blue-light"
                      >
                        {selectedDeployment?.deployment_name}
                        <LinkIcon className="text-storm-blue transition duration-300 dark:text-sky-blue-light" />
                      </Link>
                    </h2>
                    <div className="flex items-center gap-3 text-[12.5px] text-off-black-light transition duration-300 dark:text-greyed-text">
                      {lastUpdateShort && (
                        <span title={lastUpdateFull ?? undefined}>
                          {t("Last updated")} {lastUpdateShort}
                        </span>
                      )}
                      <button
                        onClick={async () =>
                          await updateAll(selectedDeployment)
                        }
                        disabled={loadingLastUpdate}
                        aria-label={t("Refresh")}
                        className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-off-white-dark transition duration-150 hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-45 dark:border-storm-blue-border dark:hover:bg-white/5"
                      >
                        <RefreshIcon />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mb-5 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-off-white-dark bg-off-white-dark transition duration-300 dark:border-storm-blue-border dark:bg-storm-blue-border sm:grid-cols-3">
                  <div className="bg-white px-6 py-4 transition duration-300 dark:bg-storm-blue-card">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-off-black-light transition duration-300 dark:text-greyed-text">
                      {t("Files in Storage")}
                    </div>
                    <div className="mt-1 text-2xl font-semibold text-primary-off-black transition duration-300 dark:text-primary-off-white">
                      {loadingFilesInStorage ? (
                        <BeatLoader
                          color={tailwindClassnames.skyBlueDark}
                          loading={true}
                          size={12}
                          margin={4}
                        />
                      ) : (
                        filesInStorage
                      )}
                    </div>
                  </div>
                  <div className="bg-white px-6 py-4 transition duration-300 dark:bg-storm-blue-card">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-off-black-light transition duration-300 dark:text-greyed-text">
                      {t("Files in Akili")}
                    </div>
                    <div className="mt-1 text-2xl font-semibold text-primary-off-black transition duration-300 dark:text-primary-off-white">
                      {loadingFilesInAkili ? (
                        <BeatLoader
                          color={tailwindClassnames.skyBlueDark}
                          loading={true}
                          size={12}
                          margin={4}
                        />
                      ) : (
                        filesInAkili
                      )}
                    </div>
                  </div>
                  <section
                    ref={lastEventTileRef}
                    tabIndex={-1}
                    aria-labelledby="last-event-label"
                    className="bg-white px-6 py-4 transition duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-storm-blue dark:focus-visible:ring-sky-blue dark:bg-storm-blue-card"
                    data-testid="last-event-tile"
                  >
                    <div
                      id="last-event-label"
                      className="text-[11px] font-semibold uppercase tracking-wider text-off-black-light transition duration-300 dark:text-greyed-text"
                    >
                      {t("Last Event")}
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      {loadingLastUpdate ? (
                        <BeatLoader
                          color={tailwindClassnames.skyBlueDark}
                          loading={true}
                          size={12}
                          margin={4}
                        />
                      ) : lastUpdate ? (
                        <>
                          <span
                            className={eventStatusChipClass(lastUpdate.status)}
                          >
                            {t(lastUpdate.type)} {t(lastUpdate.status)}
                          </span>
                          <span className="text-[12.5px] text-off-black-light transition duration-300 dark:text-greyed-text">
                            {lastUpdate.file_count ?? "—"} {t("files")} ·{" "}
                            {lastUpdate.user_name}
                          </span>
                          {showEndUpload && (
                            <button
                              type="button"
                              aria-haspopup="dialog"
                              onClick={(event) => {
                                setEndUploadOpener(event.currentTarget);
                                setEndUploadOpen(true);
                              }}
                              className={END_UPLOAD_BUTTON_CLASS}
                              data-testid="end-upload-button"
                            >
                              {t("End upload")}
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="text-2xl font-semibold text-primary-off-black transition duration-300 dark:text-primary-off-white">
                          {t("None")}
                        </span>
                      )}
                    </div>
                  </section>
                </div>
                <EndUploadModal
                  isOpen={endUploadOpen}
                  deploymentId={selectedDeployment.deployment_id}
                  fileCount={lastUpdate?.file_count}
                  opener={endUploadOpener}
                  fallbackFocusRef={lastEventTileRef}
                  onClose={() => setEndUploadOpen(false)}
                  onEnded={handleUploadEnded}
                  // A 401 from End upload means the session is over and the interceptor has
                  // already cleared the token. Send the user to the app's session-expiry page,
                  // the same route this page uses when the profile is not allowed here: it
                  // shows the interceptor's `unauthorizedMessage` and carries the sign-in
                  // button. Without it the dialog would say "sign in again" and offer no way
                  // to (Copilot review, cycle 9).
                  onSessionExpired={() =>
                    navigate("/unauthorized", { replace: true })
                  }
                />
                <div className="w-full">
                  {selectedProject?.project_id &&
                    selectedDeployment?.deployment_id && (
                      <div>
                        <UploadComponent
                          projectId={selectedDeployment?.project_id}
                          projectGuid={selectedProject?.guid}
                          deploymentId={selectedDeployment?.deployment_id}
                          deploymentName={selectedDeployment?.deployment_name}
                          deploymentGuid={selectedDeployment?.guid}
                          fetchUpdate={async () =>
                            await updateAll(selectedDeployment)
                          }
                          canUpload={canUpload}
                        />
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                          <button
                            onClick={async () => await handleImport()}
                            className={tailwindClassnames.btnPrimary}
                            disabled={!canImport || importing}
                          >
                            {importing ? (
                              <div className="place-content-center px-2">
                                <BeatLoader
                                  color={tailwindClassnames.skyBlueDark}
                                  loading={true}
                                  size={10}
                                  margin={5}
                                />
                              </div>
                            ) : (
                              t("Start Import")
                            )}
                          </button>
                          <span className={tailwindClassnames.helperText}>
                            {importHelperText}
                          </span>
                        </div>
                        {inferenceInProgress && (
                          <div
                            className={`mt-2 ${tailwindClassnames.helperText}`}
                          >
                            {t(
                              "An inference task is currently in progress for this deployment. Please try again when it is complete.",
                            )}
                          </div>
                        )}
                        <DeploymentEventHistory
                          selectedDeployment={selectedDeployment}
                          eventHistory={eventHistory}
                          loadingEventHistory={loadingEventHistory}
                        />
                      </div>
                    )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="sweet-loading flex justify-center w-full">
            <BeatLoader
              color={tailwindClassnames.skyBlueDark}
              loading={true}
              size={15}
              margin={5}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaManagement;
